import { sanitizeDeep } from './sanitize';
const getProjectDocRef = (firebase, franchiseeId, projectId) => {
  if (!firebase?.db || !franchiseeId || !projectId) return null;
  return firebase.db
    .collection('franchisees')
    .doc(franchiseeId)
    .collection('studioProjects')
    .doc(projectId);
};

const removeUndefined = (data = {}) =>
  Object.keys(data).reduce((acc, key) => {
    const value = data[key];
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const stripNonJson = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value.map(stripNonJson).filter((item) => item !== undefined);
  }
  if (isPlainObject(value)) {
    const next = {};
    Object.keys(value).forEach((key) => {
      const cleaned = stripNonJson(value[key]);
      if (cleaned !== undefined) {
        next[key] = cleaned;
      }
    });
    return next;
  }
  if (typeof value === 'function') return undefined;
  if (typeof value === 'object') return undefined;
  return value;
};

const projectCache = new Map();

const fetchProjectData = async (firebase, franchiseeId, projectId) => {
  const docRef = getProjectDocRef(firebase, franchiseeId, projectId);
  if (!docRef) return null;
  const snapshot = await docRef.get();
  if (!snapshot.exists) return null;
  return snapshot.data();
};

export const getProject = async (firebase, franchiseeId, projectId) => {
  if (!firebase) return null;
  if (projectCache.has(projectId)) {
    return { id: projectId, ...projectCache.get(projectId) };
  }
  const data = await fetchProjectData(firebase, franchiseeId, projectId);
  if (!data) return null;
  projectCache.set(projectId, data);
  return { id: projectId, ...data };
};

export const createProject = async (firebase, franchiseeId, projectId, data) => {
  const docRef = getProjectDocRef(firebase, franchiseeId, projectId);
  if (!docRef) {
    throw new Error('Studio project path is unavailable.');
  }
  const payload = stripNonJson(sanitizeDeep({ ...data, id: projectId }));
  await docRef.set(payload, { merge: true });
  projectCache.set(projectId, payload);
  return { id: projectId };
};

export const updateProject = async (firebase, franchiseeId, projectId, patch) => {
  const docRef = getProjectDocRef(firebase, franchiseeId, projectId);
  if (!docRef) {
    throw new Error('Studio project path is unavailable.');
  }
  const existing =
    projectCache.get(projectId) || (await fetchProjectData(firebase, franchiseeId, projectId));
  if (!existing) {
    throw new Error('Studio design data missing in Firebase.');
  }
  const serverTimestamp = firebase?.fieldValue?.serverTimestamp?.() || new Date().toISOString();
  const sanitized = sanitizeDeep({
    ...existing,
    ...patch,
    updatedAt: serverTimestamp,
  });
  const payload = removeUndefined(stripNonJson(sanitized || {}));
  if (Object.keys(payload).length === 0) return { id: projectId };
  projectCache.set(projectId, payload);
  await docRef.set(payload, { merge: true });
  return { id: projectId };
};
